import { notNil } from '@sixfold/typed-primitives';
import classnames from 'classnames';
import React, { useRef } from 'react';

import { TimelineTick, TimelineTickType, KeyboardKey } from './entities';
import { TrackingSegment } from '../../../tour/entities';
import { GhostVehicleColors } from '../../../vehicle/components/vehicles_layer';

interface Props {
  vehicleIndex?: number;
  sliderTicks: TimelineTick[];
  range: {
    value: number | undefined;
    min: number;
    max: number;
    step: number;
  };
  onChange: (selectedIndex: number, event: React.ChangeEvent<HTMLInputElement>) => void;
  onArrowNavigation?: (
    { currentIndex, keyPressed }: { currentIndex: number; keyPressed: KeyboardKey },
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => void;
  onTickClick: (timelineIndex: number) => void;
  trackingSegments?: TrackingSegment[];
  timelineBoundaries?: {
    startTime: string;
    endTime: string;
  };
}

export const MapSliderTimeline: React.FC<Props> = (props) => {
  const { sliderTicks, range, onChange, onArrowNavigation, onTickClick, trackingSegments, timelineBoundaries } = props;
  const timelineInput = useRef<HTMLInputElement>(null);

  const trackingOverlayGradient = calculateTrackingRangeOverlay(trackingSegments, timelineBoundaries);

  return (
    <div className="map-slider">
      <ol className="map-slider__ticks-container">
        {trackingOverlayGradient && (
          <div
            className="map-slider__tracking-range-overlay"
            style={{
              background: trackingOverlayGradient,
            }}
          />
        )}
        {sliderTicks.map((tick, idx) => {
          switch (tick.type) {
            case TimelineTickType.TOUR_EVENTS_RANGE:
            case TimelineTickType.TIMESLOT_LOADING_RANGE:
            case TimelineTickType.TIMESLOT_UNLOADING_RANGE:
            case TimelineTickType.BREAK_RANGE:
            case TimelineTickType.REST_BREAK_RANGE:
            case TimelineTickType.WEEKEND_BREAK_RANGE:
            case TimelineTickType.STOP_ZONE_RANGE:
            case TimelineTickType.TELEMETRY_CHUNK_RANGE:
              if (tick.width === 0) {
                return;
              }

              return (
                <li
                  key={idx}
                  onClick={() => {
                    if (
                      tick.type !== TimelineTickType.TELEMETRY_CHUNK_RANGE &&
                      timelineInput &&
                      timelineInput.current
                    ) {
                      onTickClick(tick.timelineIndex);
                      timelineInput.current.focus();
                    }
                  }}
                  className={classnames({
                    'map-slider__tick-range': true,
                    'map-slider__tick-tooltip': tick.label,
                    'map-slider__tick-range--events': tick.type === TimelineTickType.TOUR_EVENTS_RANGE,
                    'map-slider__tick-range--timeslot-loading': tick.type === TimelineTickType.TIMESLOT_LOADING_RANGE,
                    'map-slider__tick-range--timeslot-unloading':
                      tick.type === TimelineTickType.TIMESLOT_UNLOADING_RANGE,
                    'map-slider__tick-range--small-break': tick.type === TimelineTickType.BREAK_RANGE,
                    'map-slider__tick-range--rest-break': tick.type === TimelineTickType.REST_BREAK_RANGE,
                    'map-slider__tick-range--we-break': tick.type === TimelineTickType.WEEKEND_BREAK_RANGE,
                    'map-slider__tick-range--stop': tick.type === TimelineTickType.STOP_ZONE_RANGE,
                    'map-slider__tick-range--telemetry-chunk': tick.type === TimelineTickType.TELEMETRY_CHUNK_RANGE,
                  })}
                  aria-label={tick.label}
                  style={{
                    left: `${tick.position}%`,
                    width: `${tick.width}%`,
                    cursor: tick.type !== TimelineTickType.TELEMETRY_CHUNK_RANGE ? 'pointer' : 'default',
                    pointerEvents: tick.label === undefined ? 'none' : 'initial',
                  }}
                />
              );

            case TimelineTickType.EVENT:
              return (
                <li
                  key={idx}
                  onClick={() => {
                    const timelineInputCurrent = timelineInput?.current;
                    if (notNil(timelineInputCurrent)) {
                      onTickClick(tick.timelineIndex);
                      timelineInputCurrent.focus();
                    }
                  }}
                  className={classnames({
                    'map-slider__tick-event': true,
                    'map-slider__tick-tooltip': true,
                    'map-slider__tick-event--tractor-switch':
                      tick.label !== undefined && tick.label.includes('tractor'),
                  })}
                  style={{ left: `${tick.position}%` }}
                  aria-label={tick.label}>
                  ●
                </li>
              );
            case TimelineTickType.EXTERNAL_EVENT:
              return (
                <li
                  key={idx}
                  onClick={() => {
                    const timelineInputCurrent = timelineInput?.current;
                    if (notNil(timelineInputCurrent)) {
                      onTickClick(tick.timelineIndex);
                      timelineInputCurrent.focus();
                    }
                  }}
                  className={classnames({
                    'map-slider__tick-external-event': true,
                    'map-slider__tick-tooltip': true,
                    [`map-slider__tick-external-event--${tick.subtype}`]: tick.subtype,
                  })}
                  style={{ left: `${tick.position}%` }}
                  aria-label={tick.label}>
                  ▲
                </li>
              );
            default:
              return;
          }
        })}
      </ol>
      <input
        ref={timelineInput}
        type="range"
        value={range.value}
        min={range.min}
        max={range.max}
        step={range.step}
        disabled={onChange === undefined}
        onClick={() => {
          const timelineInputCurrent = timelineInput?.current;
          if (notNil(timelineInputCurrent)) {
            // Safari does not register timeline focus on click otherwise
            timelineInputCurrent.focus();
          }
        }}
        onKeyDown={(e) => {
          if (
            onArrowNavigation !== undefined &&
            (e.key === KeyboardKey.ARROW_LEFT || e.key === KeyboardKey.ARROW_RIGHT)
          ) {
            e.preventDefault();
            onArrowNavigation(
              {
                currentIndex: parseInt(e.currentTarget.value, 10),
                keyPressed: e.key,
              },
              e,
            );
          }
        }}
        onChange={(e) => onChange(parseInt(e.currentTarget.value, 10), e)}
        className={'map-slider__input map-slider-vehicle__input'}
        style={{
          color:
            notNil(props.vehicleIndex) && props.vehicleIndex < GhostVehicleColors.length
              ? `rgba(${GhostVehicleColors[props.vehicleIndex][0]},${GhostVehicleColors[props.vehicleIndex][1]},${
                  GhostVehicleColors[props.vehicleIndex][2]
                }, 1)`
              : 'transparent',
        }}
      />
    </div>
  );
};

interface ParsedTrackingSegment {
  vehicleId: string;
  startMs: number;
  endMs: number;
}

interface ParsedTimelineBoundaries {
  startMs: number;
  endMs: number;
  rangeMs: number;
}

function parseTrackingSegment(segment: TrackingSegment): ParsedTrackingSegment {
  return {
    vehicleId: segment.vehicleId,
    startMs: new Date(segment.trackingStartDate).getTime(),
    endMs: new Date(segment.trackingEndDate).getTime(),
  };
}

function parseTimelineBoundaries(boundaries: { startTime: string; endTime: string }): ParsedTimelineBoundaries {
  const startMs = new Date(boundaries.startTime).getTime();
  const endMs = new Date(boundaries.endTime).getTime();
  return {
    startMs,
    endMs,
    rangeMs: endMs - startMs,
  };
}

function calculateTrackingRangeOverlay(
  trackingSegments: TrackingSegment[] | undefined,
  timelineBoundaries: { startTime: string; endTime: string } | undefined,
): string | undefined {
  if (!trackingSegments || trackingSegments.length === 0 || !timelineBoundaries) {
    return undefined;
  }

  const NON_TRACKING_COLOR = 'rgba(128, 128, 128, 0.5)';
  const TRACKING_COLOR = 'transparent';

  const timeline = parseTimelineBoundaries(timelineBoundaries);

  if (timeline.rangeMs <= 0) {
    return undefined;
  }

  const parsedSegments = trackingSegments.map(parseTrackingSegment);
  const sortedSegments = [...parsedSegments].sort((a, b) => a.startMs - b.startMs);

  const gradientStops: string[] = [];

  const firstSegmentStart = sortedSegments[0].startMs;
  if (firstSegmentStart > timeline.startMs) {
    const firstSegmentStartPercent = ((firstSegmentStart - timeline.startMs) / timeline.rangeMs) * 100;
    gradientStops.push(`${NON_TRACKING_COLOR} 0%`);
    gradientStops.push(`${NON_TRACKING_COLOR} ${firstSegmentStartPercent}%`);
  }

  sortedSegments.forEach((segment, idx) => {
    const segmentStartPercent = Math.max(0, ((segment.startMs - timeline.startMs) / timeline.rangeMs) * 100);
    const segmentEndPercent = Math.min(100, ((segment.endMs - timeline.startMs) / timeline.rangeMs) * 100);

    if (segmentStartPercent > 0 || gradientStops.length > 0) {
      gradientStops.push(`${TRACKING_COLOR} ${segmentStartPercent}%`);
    }
    gradientStops.push(`${TRACKING_COLOR} ${segmentEndPercent}%`);

    if (idx < sortedSegments.length - 1) {
      const nextSegmentStart = sortedSegments[idx + 1].startMs;
      const nextSegmentStartPercent = ((nextSegmentStart - timeline.startMs) / timeline.rangeMs) * 100;

      if (nextSegmentStartPercent > segmentEndPercent) {
        gradientStops.push(`${NON_TRACKING_COLOR} ${segmentEndPercent}%`);
        gradientStops.push(`${NON_TRACKING_COLOR} ${nextSegmentStartPercent}%`);
      }
    }
  });

  const lastSegment = sortedSegments[sortedSegments.length - 1];
  if (lastSegment.endMs < timeline.endMs) {
    const lastSegmentEndPercent = ((lastSegment.endMs - timeline.startMs) / timeline.rangeMs) * 100;
    gradientStops.push(`${NON_TRACKING_COLOR} ${lastSegmentEndPercent}%`);
    gradientStops.push(`${NON_TRACKING_COLOR} 100%`);
  }

  return gradientStops.length > 0 ? `linear-gradient(to right, ${gradientStops.join(', ')})` : undefined;
}
